/**
 * Read a base folder into `{ meta, config }` — the `push` half of the round-trip
 * `config-writer.ts` owns the other end of.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { expected } from '../../lib/expected.js';
import { isDirectory } from '../../lib/layout.js';
import { ensureRealSubdirNoSymlink, readFileNoFollow } from '../../lib/fs-safety.js';
import { BASE_META_FILE, hasBaseMetaFile, readBaseMeta } from '../../lib/base-workspace.js';
import {
  ENTITY_DIRS,
  type ActionConfig,
  type BaseConfig,
  type BaseMeta,
  type EntityKind,
  type InboundWebhookConfig,
  type RecordTypeConfig,
  type RelationshipTypeConfig,
  type SeedConfig,
  type ToolsetConfig,
  type TriggerConfig,
} from './types.js';

export interface ParsedBase {
  meta: BaseMeta;
  config: BaseConfig;
}

/**
 * Parse one entity subdir. Returned as `unknown[]` so each caller asserts its
 * own concrete kind with a single cast at the parse boundary.
 */
function readEntityDir(folder: string, dir: string): unknown[] {
  // Bounded by the base folder, and no symlink is followed on the way in: a
  // committed `record-types/x.yaml -> ~/.ssh/id_rsa` would otherwise be parsed
  // as config and sent to the server by the next push.
  //
  // A symlinked DIRECTORY is REFUSED, not treated as empty — for the same reason
  // the file branch below refuses rather than skips. This config is the full
  // desired state, so reading `record-types -> /shared/types` as "no record
  // types" makes the next `push --prune` delete every record type on the base,
  // cascading to their records. `ensureRealSubdirNoSymlink` answers true for a
  // path that simply does not exist, so a base with no triggers still returns
  // the empty list below rather than reaching this throw.
  if (!ensureRealSubdirNoSymlink(folder, dir, false)) {
    throw expected(`Refusing to read ${dir}: it is a symlink, not a config directory.`);
  }
  if (!isDirectory(dir)) return [];
  const out: Record<string, unknown>[] = [];
  for (const file of fs.readdirSync(dir).sort()) {
    if (!file.endsWith('.yaml')) continue;
    const abs = path.join(dir, file);
    const bytes = readFileNoFollow(folder, abs);
    if (bytes === null) {
      // A symlink, or something that is not a regular file. Refused rather than
      // skipped: silently omitting it would read as a deletion in the diff.
      throw expected(`Refusing to read ${abs}: it is a symlink, not a config file.`);
    }
    let parsed: unknown;
    try {
      parsed = yaml.load(bytes.toString('utf-8'));
    } catch (err) {
      throw expected(`Failed to parse ${abs}: ${err instanceof Error ? err.message : String(err)}`);
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw expected(`${abs} is empty or not a YAML object`);
    }
    const entity = parsed as Record<string, unknown>;
    const stem = path.basename(file, '.yaml');
    // The `id` field is authoritative; a filename that drifted from it is an
    // error rather than a silent rename, because the server keys on the id.
    if (entity.id === undefined) entity.id = stem;
    else if (String(entity.id) !== stem) {
      throw expected(`${abs}: id "${String(entity.id)}" does not match filename "${stem}.yaml"`);
    }
    out.push(entity);
  }
  return out;
}

/**
 * Read a base folder. Each absent entity subdir yields an empty array — the
 * config is the FULL desired state, so an omitted entity reads as a removal in
 * the diff (and is only actioned by `push --prune`).
 */
export function parseBaseFolder(folder: string): ParsedBase {
  const meta = readBaseMeta(folder);
  if (!meta) {
    // Present-but-unreadable and absent are different problems with different
    // fixes; reporting the first as the second sends the user looking for a file
    // that is sitting right there.
    throw expected(
      hasBaseMetaFile(folder)
        ? `${path.join(folder, BASE_META_FILE)} could not be parsed as a YAML mapping. Fix it, or delete it and pull again.`
        : `No ${BASE_META_FILE} found in ${folder}`,
    );
  }

  const read = (kind: EntityKind) => readEntityDir(folder, path.join(folder, ENTITY_DIRS[kind]));
  const config: BaseConfig = {
    record_types: read('record_types') as RecordTypeConfig[],
    relationship_types: read('relationship_types') as RelationshipTypeConfig[],
    inbound_webhooks: read('inbound_webhooks') as InboundWebhookConfig[],
    triggers: read('triggers') as TriggerConfig[],
    toolsets: read('toolsets') as ToolsetConfig[],
    actions: read('actions') as ActionConfig[],
    seeds: read('seeds') as SeedConfig[],
  };
  return { meta, config };
}
